// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Features from './components/Features';
import Pricing from './components/Pricing';
import Stats from './components/Stats';
import Contact from './components/Contact';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import LoadingScreen from './components/LoadingScreen';
import ScrollToTop from './components/ScrollToTop';
import Notification from './components/Notification';
import { HelmetProvider } from 'react-helmet-async';
import SEO from './components/SEO';
import { SpeedInsights } from '@vercel/speed-insights/react';
import ProxyGenerator from './components/ProxyGenerator';
import ThankYou from './components/ThankYou';
import RdpPage from './components/RdpPage';
import Maintenance from './components/Maintenance';

// ⚡ EASY TRIGGER: Set to true to enable maintenance mode
const MAINTENANCE_MODE = false;


// Move ReferralModal outside of the App component
// Updated ReferralModal Component
const ReferralModal = ({
  email,
  setEmail,
  referrer,
  handleReferralSubmit,
  loading,
  responseMessage, // New prop for displaying response messages
  isError,          // New prop to indicate if the message is an error
}) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
    <div className="bg-dark-secondary p-8 rounded-2xl max-w-md w-full border border-accent/20 shadow-2xl animate-float relative">
      {/* Glowing accent line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-1 bg-accent animate-glow rounded-full"></div>

      <div className="space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-text-primary">
            Welcome to NexaProxy
          </h2>
          
        </div>

        {/* Response Message or Email Input */}
        {responseMessage ? (
          // Display response message with appropriate icon
          <div className="flex items-center justify-center space-x-2">
            {isError ? (
              <span className="text-red-500 text-3xl">✖</span>
            ) : (
              <span className="text-green-500 text-3xl">✔</span>
            )}
            <p className="text-text-primary text-center">{responseMessage}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Email Input */}
            <p className="text-accent">You've Been Invited by: {referrer}</p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email here to accept Invitation"
              className="w-full p-3 bg-dark-bg border border-accent/20 rounded-lg text-text-primary placeholder-text-secondary focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all duration-300"
              disabled={loading}
            />

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => handleReferralSubmit(email)}
                className="flex-1 px-4 py-3 bg-accent rounded-lg text-white font-semibold hover:bg-accent-hover transition-all duration-300"
                disabled={loading || !email}
              >
                {loading ? 'Processing...' : 'Accept Invitation'}
              </button>
            </div>

            {/* Loading Indicator */}
            {loading && (
              <div className="flex justify-center">
                <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-8 w-8"></div>
              </div>
            )}

            {/* Partner Benefits Card */}
            <div className="bg-dark-bg p-4 rounded-lg border border-accent/10">
              <h3 className="text-lg font-semibold text-accent mb-2">
                Partner Benefits
              </h3>
              <ul className="space-y-2 text-text-secondary">
                <li className="flex items-center">
                  <span className="mr-2">💰</span>
                  Earn 10% commission on all referrals
                </li>
                <li className="flex items-center">
                  <span className="mr-2">📅</span>
                  Weekly commission payouts
                </li>
                <li className="flex items-center">
                  <span className="mr-2">🎁</span>
                  Top partners receive merchandise: t-shirts, mugs, hoodies, etc.
                </li>
              </ul>

              {/* Centered Learn More Button */}
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => window.open('/partner-program')}
                  className="px-4 py-3 bg-color-bg rounded-lg text-white font-semibold hover:bg-accent-hover transition-all duration-300"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

const App = () => {
  const [notification, setNotification] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referrer, setReferrer] = useState('');
  const [email, setEmail] = useState(''); // Email state
  const [loading, setLoading] = useState(false); // Loading state
  const [responseMessage, setResponseMessage] = useState(''); // Response message
  const [isError, setIsError] = useState(false); // Error indicator

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      setReferrer(refCode);
      setShowReferralModal(true);
    }
  }, []);

  // Handle referral submission
  const handleReferralSubmit = async (submittedEmail) => {
    try {
      setLoading(true);
      // Add API call to save referral
      const response = await fetch(
        'https://api.partner.nexaproxy.com/api/invitations/accept',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: submittedEmail,
            referrer, // Ensure this is the referrer's username
          }),
        }
      );

      if (!response.ok) {
        // Read error message from response
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Error processing referral';
        setIsError(true);
        setResponseMessage(errorMessage);
      } else {
        // Show success message
        setIsError(false);
        setResponseMessage('Thank you for accepting the invitation!');
      }
    } catch (error) {
      // Handle network errors or other unexpected errors
      console.error('Referral submission error:', error);
      setIsError(true);
      setResponseMessage('Error processing referral');
    } finally {
      setLoading(false);
      // Close the modal after a short delay to allow user to see the message
      setTimeout(() => {
        setShowReferralModal(false);
        setResponseMessage('');
        setIsError(false);
        setEmail('');
      }, 3000);
    }
  };

  // If maintenance mode is enabled, show only the maintenance page
  if (MAINTENANCE_MODE) {
    return <Maintenance />;
  }

  return (
    <Router>
    <HelmetProvider>
      <div className="relative">
        {showReferralModal && (
          <ReferralModal
            email={email}
            setEmail={setEmail}
            referrer={referrer}
            handleReferralSubmit={handleReferralSubmit}
            loading={loading}
            responseMessage={responseMessage}
            isError={isError}
          />
        )}
        <SpeedInsights />
        <AnimatePresence>
          {notification && (
            <Notification
              message={notification.message}
              type={notification.type}
              onClose={() => setNotification(null)}
            />
          )}
        </AnimatePresence>

        <Routes>
            <Route
              path="/"
              element={
                <div className="min-h-screen bg-dark-bg">
                  <SEO />
                  <Navbar />
                  <Hero />
                  <Features />
                  <Pricing showNotification={showNotification} />
                  <Stats />
                  <Contact showNotification={showNotification} />
                  <Footer />
                  <ScrollToTop />
                </div>
              }
            />
            <Route path="/generate" element={<ProxyGenerator />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/rdp" element={<RdpPage />} />
          </Routes>
        </div>
      </HelmetProvider>
    </Router>
  );
};

export default App;